import { motion, useReducedMotion } from "framer-motion";
import Reveal from "./Reveal";
import { Star, Dot, Squiggle, Spiral, Blob } from "./doodles";

const blocks = [
  {
    title: "THINK",
    copy: "Beyond the obvious",
    cls: "bg-grape text-navy",
    deco: "star",
    decoColor: "text-sunny",
  },
  {
    title: "TALK",
    copy: "About the uncomfortable stuff",
    cls: "bg-sunny text-navy",
    deco: "squiggle",
    decoColor: "text-navy/60",
  },
  {
    title: "PLAY",
    copy: "Because why not!",
    cls: "bg-blush text-navy",
    deco: "blob",
    decoColor: "text-paper",
  },
  {
    title: "CREATE",
    copy: "Without overthinking it",
    cls: "bg-skyblue text-navy",
    deco: "spiral",
    decoColor: "text-paper",
  },
];

const Deco = ({ kind, className }) => {
  if (kind === "star") return <Star className={`h-7 w-7 md:h-9 md:w-9 ${className}`} aria-hidden="true" />;
  if (kind === "dot") return <Dot className={`h-4 w-4 md:h-5 md:w-5 ${className}`} aria-hidden="true" />;
  if (kind === "blob") return <Blob className={`float-a h-8 w-8 md:h-10 md:w-10 ${className}`} aria-hidden="true" />;
  if (kind === "spiral") return <Spiral className={`h-9 w-9 md:h-11 md:w-11 ${className}`} aria-hidden="true" />;
  return <Squiggle className={`w-16 md:w-20 ${className}`} aria-hidden="true" />;
};

export default function Blocks() {
  const reduce = useReducedMotion();
  return (
    <section data-testid="blocks-section" className="py-16 md:py-24" aria-label="What could paper become">
      <div className="mx-auto max-w-[1440px] px-5 md:px-12 lg:px-20">
        <Reveal>
          <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.2em] text-navy/60" data-testid="blocks-eyebrow">
            What we&rsquo;re making
          </p>
          <h2
            className="font-display text-[clamp(2.4rem,5.5vw,5rem)] font-semibold leading-[1.02] tracking-tight text-navy"
            data-testid="blocks-headline"
          >
            What if paper could <span className="text-grape">say the unsaid?</span>
          </h2>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-12 md:gap-6">
          {blocks.map((b, i) => (
            <motion.div
              key={b.title}
              data-testid={`block-${b.title.toLowerCase()}`}
              initial={reduce ? false : { opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={reduce ? undefined : { scale: 1.03 }}
              className={`group flex flex-col gap-6 overflow-hidden rounded-[2rem] p-7 md:rounded-[2.5rem] md:p-8 ${b.cls}`}
            >
              <div className="flex items-start justify-between">
                <h3 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">{b.title}</h3>
                <span className="transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                  <Deco kind={b.deco} className={b.decoColor} />
                </span>
              </div>
              <p className="relative max-w-xs text-base font-bold md:text-lg">
                {b.copy}
                <span className="absolute -bottom-1 left-0 h-[3px] w-0 rounded-full bg-current transition-[width] duration-500 group-hover:w-full" />
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
